// Route-specific skeleton loaders - modular and maintainable
import React from 'react';
import {
  SkeletonDashboard,
  SkeletonSupport,
  SkeletonUploads,
  SkeletonSharing,
  SkeletonSettings,
  SkeletonBilling,
  Skeleton,
  SkeletonCard,
} from './Loading';

/**
 * Get the appropriate skeleton loader based on the route path
 */
export const getSkeletonForRoute = (path: string): React.ReactNode => {
  if (path.startsWith('/support')) {
    return <SkeletonSupport />;
  }
  
  if (path.startsWith('/dashboard') || path === '/') {
    return <SkeletonDashboard />;
  }
  
  if (path.startsWith('/upload')) {
    return <SkeletonUploads />;
  }
  
  if (path.startsWith('/sharing')) {
    return <SkeletonSharing />;
  }
  
  if (path.startsWith('/settings')) {
    return <SkeletonSettings />;
  }
  
  if (path.startsWith('/billing')) {
    return <SkeletonBilling />;
  }
  
  if (path.startsWith('/cloud-explorer') || path.startsWith('/cloud')) {
    return <CloudExplorerSkeleton />;
  }
  
  // Default generic skeleton for other routes
  return <DefaultSkeleton />;
};

/**
 * Cloud Explorer specific skeleton loader
 */
const CloudExplorerSkeleton: React.FC = () => (
  <div className="@container cloud-explorer-content space-y-3 md:space-y-4 lg:space-y-6 w-full min-w-0 overflow-x-hidden">
    {/* Header */}
    <div className="flex flex-col gap-3 md:gap-4">
      <div className="flex flex-col @explorer:flex-row @explorer:items-start @explorer:justify-between gap-3 @explorer:gap-4">
        <div className="flex-1 min-w-0">
          <Skeleton height="32px" width="180px" className="mb-1" />
          <Skeleton height="20px" width="280px" />
        </div>
        <div className="flex items-center gap-2 md:gap-3 flex-wrap">
          <Skeleton height="44px" width="120px" rounded="xl" />
          <Skeleton height="44px" width="100px" rounded="xl" />
        </div>
      </div>
    </div>

    {/* Filters – mobile sheet button vs desktop bar */}
    <div className="@explorer:hidden">
      <Skeleton height="44px" width="100%" rounded="lg" />
    </div>
    <div className="hidden @explorer:block bg-dark-secondary/40 border border-white/10 rounded-xl p-4">
      <div className="flex flex-wrap items-center gap-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} height="36px" width="100px" rounded="lg" />
        ))}
      </div>
    </div>

    {/* Folders + Files – vertical stack matching live layout */}
    <div className="flex flex-col gap-4 lg:gap-6">
      <div className="space-y-3">
        <Skeleton height="24px" width="100px" />
        <div className="grid grid-cols-1 sm:grid-cols-2 @explorer:grid-cols-3 2xl:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} height="72px" width="100%" rounded="xl" />
          ))}
        </div>
      </div>
      <div className="space-y-3">
        <Skeleton height="24px" width="80px" className="hidden @explorer:block" />
        <div className="bg-dark-secondary/40 border border-white/5 rounded-xl overflow-hidden">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-3 border-b border-white/5 last:border-0">
              <Skeleton circle width="32px" height="32px" />
              <div className="flex-1 space-y-2">
                <Skeleton height="14px" width="70%" />
                <Skeleton height="12px" width="40%" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

/**
 * Default generic skeleton loader for unknown routes
 */
const DefaultSkeleton: React.FC = () => (
  <div className="max-w-6xl mx-auto space-y-6 w-full min-w-0 overflow-x-hidden">
    <div className="flex items-center justify-between">
      <div className="space-y-3">
        <Skeleton height="28px" width="180px" />
        <Skeleton height="18px" width="260px" />
      </div>
      <div className="flex items-center gap-2">
        <Skeleton height="40px" width="110px" rounded="xl" />
        <Skeleton height="40px" width="110px" rounded="xl" />
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <SkeletonCard />
      <SkeletonCard />
      <SkeletonCard className="hidden lg:block" />
    </div>

    <div className="bg-dark-secondary/50 border border-white/5 rounded-xl p-5 space-y-4">
      <Skeleton height="22px" width="220px" />
      <div className="space-y-3">
        <Skeleton height="12px" width="100%" />
        <Skeleton height="12px" width="90%" />
        <Skeleton height="12px" width="96%" />
        <Skeleton height="12px" width="85%" />
      </div>
    </div>
  </div>
);

